import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom, take } from 'rxjs';
import { ADMIN_PANEL_ROLES } from '../../enums/permission/admin-panel-roles';
import { SecurityStateAction } from '../../state/security-state/security-state.actions';
import { installationPermissionsIsLoadingSelector, systemPermissionsSelector } from '../../state/security-state/security-state.selectors';

/**
 * Gate for the whole `/admin` area. The area is global (no installation
 * context), so we do NOT use the installation-centric `PermissionGuard`. Instead we mirror its
 * permission-load-then-check flow but gate purely on `selectCanAccessContent` at System level.
 *
 * The permission list is read directly, NOT through `selectCanAccessContent`: that selector always
 * grants a plain system Admin, and since 2026-09-07 the panel's API endpoints are gated on
 * BreeceSupport - which `ValidateRole` treats as mutually exclusive with Admin. An Admin let in
 * here would reach a panel where every request answers 403, so the gate has to be as strict as the
 * API's. Same inline idiom as the sidebar's role label (admin-sidebar.component.ts).
 *
 * We load permissions via the SYSTEM-scoped call (`getSystemPermissions`), NOT the
 * installation-scoped one: this route has no active installation, so the installation-scoped
 * `/security/permissions` endpoint (RoleLevel.InstallationOrGroup) fails EntityOwnerFilter on the
 * API with "InstallationId and GroupId is missing". `getSystemPermissions` hits `GET /security`
 * (RoleLevel.System) with no RefCode header.
 */
export const adminPanelGuard: CanActivateFn = async () => {
  const store = inject(Store);
  const router = inject(Router);

  // Ensure system permissions are loaded (deep-link / fresh session), then wait for the load to settle.
  store.dispatch(SecurityStateAction.getSystemPermissions());
  await firstValueFrom(
    store.select(installationPermissionsIsLoadingSelector).pipe(
      filter((loading) => loading === false),
      take(1)
    )
  );

  const systemPermissions = await firstValueFrom(store.select(systemPermissionsSelector));
  const held = systemPermissions?.permissions ?? [];
  const canAccess = ADMIN_PANEL_ROLES.some(role => held.includes(role));

  return canAccess ? true : router.createUrlTree(['access-denied']);
};
